import { useLingui } from '@lingui/react/macro';
import { Header } from './header';
import { Layout } from './layout';
import { Seo } from '@/routes/-components/seo';
import { diamondGlows, DiamondGrid, Eyebrow, PillLink } from './primitives';

/**
 * The redesign's 404. Its own component rather than a route body because two entry points render it:
 * the `/new/$` catch-all for a URL that matches nothing, and the route group's `notFoundComponent` for
 * a `notFound()` raised inside a page that does exist.
 *
 * No footer, as the hand-off draws it: the page's whole job is the two links back into the site, and a
 * footer full of alternatives underneath would bury them.
 */
export function NotFoundPage() {
	const { t } = useLingui();
	return (
		<Layout>
			<Header />
			{/* `noindex`, and no canonical or hreflang: there is no page at this URL to point anywhere. The
			    HTTP 404 says so too — see the loader in new/$.tsx — but a crawler that ignores the status
			    still must not index this. */}
			<Seo title={t`Page not found - Worldcoo`} noindex />
			{/* The hand-off's card is 676 wide at 1440 with 32px page padding, which is exactly half the
			    content width once the 24px gutter is taken out: 1440 - 64 - 24 = 1352, or 676 a side. */}
			<section className="mx-auto grid max-w-[1440px] items-center gap-6 px-6 pt-6 pb-16 md:pb-24 lg:grid-cols-2 xl:px-8">
				<div className="bg-sage relative aspect-[676/629] overflow-hidden rounded-3xl">
					{/* One glow at 7.2%/97.2% of the card — the centre of the hand-off's own gradient, which
					    is why the dots gather in the bottom-left corner and nowhere else. */}
					<DiamondGrid
						color="#35433F"
						opacity={1}
						size={4.39}
						gap={27.59}
						masks={[diamondGlows({ x: '7.2%', y: '97.2%' })]}
					/>
					{/* Drawn at the card's own 676x629 viewBox, so it sits where Figma places it and scales
					    with the card instead of needing its own offsets. */}
					<img
						src="/redesign/not-found/lost-folder.svg"
						alt={t`An open folder with two butterflies flying out of it`}
						className="absolute inset-0 size-full"
					/>
				</div>

				<div className="flex flex-col items-start gap-6 lg:mx-auto lg:max-w-[486px]">
					<Eyebrow>404</Eyebrow>
					<h1 className="text-4xl leading-[1.14] font-medium tracking-[-0.02em] md:text-[48px]">
						{t`It looks like this page got lost.`}
					</h1>
					<p className="text-ink/70 leading-[1.5]">
						{t`The page you are looking for is not available. We invite you to carry on discovering how small actions can add up to big change.`}
					</p>
					<div className="flex flex-wrap gap-3">
						<PillLink variant="ink" to="/new/solutions">
							{t`Solutions`}
						</PillLink>
						<PillLink variant="ink" to="/new/partner-ngos">
							{t`Partner NGOs`}
						</PillLink>
					</div>
				</div>
			</section>
		</Layout>
	);
}
